"use client"

import { useState, useEffect } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { Menu, X, User, LogOut, ArrowRight, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/use-auth"
import { usePortal } from "@/hooks/use-portal"
import { signOut } from "firebase/auth"
import { auth } from "@/lib/firebase"
import { motion, AnimatePresence } from "framer-motion"

const Navbar = () => {
  const { user, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { portal, setPortal } = usePortal()
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [profileMenuOpen, setProfileMenuOpen] = useState(false)
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<string | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false)
    setProfileMenuOpen(false)
    setExpandedMobileMenu(null)
  }, [location.pathname])

  const handleSignOut = async () => {
    await signOut(auth)
    setIsOpen(false)
    navigate("/")
  }

  const navLinks = [
    { label: "Home", href: "/" },
    { label: "Courses", href: "/#courses", items: [
      { label: "TSPSC Specialized", href: "/?portal=tspsc#courses", portal: "tspsc" },
      { label: "APPSC Specialized", href: "/?portal=appsc#courses", portal: "appsc" }
    ]},
    { label: "PYQs", href: "/?tab=pyq#resources", items: [
      { label: "TSPSC Archives", href: "/?tab=pyq#resources", portal: "tspsc" },
      { label: "APPSC Archives", href: "/?tab=pyq#resources", portal: "appsc" }
    ]},
    { label: "Resources", href: "/?tab=articles#resources", items: [
      { label: "Current Affairs", href: "/?tab=articles#resources" },
      { label: "Magazines", href: "/?tab=magazines#resources" }
    ]},
    { label: "Faculty", href: "/#faculty" },
    { label: "Rankers", href: "/#testimonials" },
  ]

  const handleAnchorClick = (e: React.MouseEvent, href: string, portal?: string) => {
    setActiveMenu(null);
    if (portal) {
      setPortal(portal as any);
    }
    if (href.includes('#')) {
      const parts = href.split('#');
      const hrefBeforeHash = parts[0];
      const id = parts[1];
      
      // Extract clean pathname without query params
      const targetPathname = hrefBeforeHash.split('?')[0] || '/';
      const cleanLocationPathname = location.pathname === '' ? '/' : location.pathname;
      
      // Check if we are on the same page
      if (cleanLocationPathname === targetPathname || (targetPathname === '/' && cleanLocationPathname === '/')) {
         // If there's a search change, we let the router handle the URL update, 
         // but we still want to scroll. However, preventing default might block the URL update if not careful.
         // Actually, if we want the URL to update WITH the search param AND scroll, 
         // we should probably let the Link component do its thing but scroll after.
         
         // If NO search change, we can prevent default and scroll immediately.
         const currentSearch = location.search;
         const targetSearch = hrefBeforeHash.includes('?') ? '?' + hrefBeforeHash.split('?')[1] : '';
         
         if (currentSearch === targetSearch) {
            e.preventDefault();
            const element = document.getElementById(id);
            if (element) {
               element.scrollIntoView({ behavior: 'smooth' });
            }
            setIsOpen(false);
         }
         // If search DOES change, we don't prevent default, 
         // so the router updates URL, and browser/app should handle scroll.
      }
    } else {
      setIsOpen(false);
    }
  }

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-[100] transition-all duration-300",
        isScrolled
          ? "bg-white border-b border-rose-700/10 shadow-lg py-1.5"
          : "bg-white border-b border-slate-100 py-2.5"
      )}
    >
      {/* Institutional Alert Bar */}
      <div className="bg-rose-950 overflow-hidden py-1.5 relative hidden sm:block">
        <div className="max-w-7xl mx-auto px-8 flex justify-between items-center relative z-10">
          <div className="flex items-center gap-3">
             <div className="w-2 h-2 rounded-full bg-rose-400 animate-pulse" />
             <p className="text-[11px] font-black text-white uppercase tracking-[0.3em]">Institutional Update: New Batch May 15th</p>
          </div>
          <div className="flex items-center gap-4 text-[10px] font-black text-rose-100 uppercase tracking-widest">
             <span className="hover:text-white cursor-pointer group flex items-center gap-1 transition-colors">Official Gazette <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" /></span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="flex justify-between items-center h-14 gap-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group min-w-0">
            <div className="w-16 h-16 sm:w-24 sm:h-24 flex items-center justify-center relative overflow-hidden shrink-0">
              <img src="/logo-final.png" alt="Rohith's Academy" className="w-full h-full object-cover" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-serif font-black text-lg sm:text-xl text-slate-900 leading-none truncate">Rohith's Academy</span>
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#b48c36] mt-1.5 truncate">Institutional Excellence</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div 
                key={link.label}
                className="relative py-4"
                onMouseEnter={() => setActiveMenu(link.label)}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <Link
                  to={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href, (link as any).portal)}
                  className={cn(
                    "text-[12px] font-black uppercase tracking-[0.2em] transition-all flex items-center gap-1.5",
                    location.pathname === link.href ? "text-[#b48c36]" : "text-slate-700 hover:text-slate-950"
                  )}
                >
                  {link.label}
                  {link.items && <ChevronDown size={10} className={cn("transition-transform", activeMenu === link.label ? "rotate-180" : "")} />}
                </Link>

                <AnimatePresence>
                  {link.items && activeMenu === link.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-1/2 -translate-x-1/2 w-52 pt-2"
                    >
                      <div className="bg-white border border-slate-100 shadow-2xl rounded-sm py-3 px-1">
                         {link.items.map((item: any) => (
                           <Link
                             key={item.label}
                             to={item.href}
                             onClick={(e) => handleAnchorClick(e, item.href, item.portal)}
                             className="block px-4 py-2 text-[11px] font-black uppercase tracking-widest text-slate-700 hover:text-slate-950 hover:bg-slate-50 transition-all"
                           >
                             {item.label}
                           </Link>
                         ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-4">

             <div className="flex items-center gap-2 relative min-w-[40px] justify-end">
               {loading ? (
                 <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center">
                    <div className="w-3 h-3 border-2 border-slate-200 border-t-[#b48c36] rounded-full animate-spin" />
                 </div>
               ) : user ? (
                 <>
                   <button 
                     onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                     className="w-9 h-9 rounded-full bg-slate-950 text-[#b48c36] flex items-center justify-center font-bold text-xs ring-1 ring-slate-200 hover:ring-[#b48c36]/40 transition-all"
                   >
                      {user.displayName ? user.displayName.charAt(0).toUpperCase() : <User size={14} />}
                   </button>

                   <AnimatePresence>
                     {profileMenuOpen && (
                       <>
                         {/* Close backdrop */}
                         <div className="fixed inset-0 z-0" onClick={() => setProfileMenuOpen(false)} />
                         
                         <motion.div
                           initial={{ opacity: 0, y: 10, scale: 0.95 }}
                           animate={{ opacity: 1, y: 0, scale: 1 }}
                           exit={{ opacity: 0, y: 10, scale: 0.95 }}
                           className="absolute top-full right-0 mt-2 w-48 bg-white border border-slate-100 shadow-2xl rounded-sm py-2 z-10"
                         >
                            <div className="px-4 py-2 border-b border-slate-50 mb-1">
                               <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Account</p>
                               <p className="text-[12px] font-bold text-slate-900 truncate">{user.email}</p>
                            </div>
                            <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 text-[12px] font-black uppercase tracking-widest text-slate-500 hover:text-[#b48c36] hover:bg-slate-50 transition-all">
                               <User size={14} /> Profile
                            </Link>
                            <button 
                              onClick={handleSignOut}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-[12px] font-black uppercase tracking-widest text-red-500 hover:bg-red-50 transition-all"
                            >
                               <LogOut size={14} /> Log Out
                            </button>
                         </motion.div>
                       </>
                     )}
                   </AnimatePresence>
                 </>
               ) : (
                  <div className="flex items-center gap-2">
                    <Link to="/login" className="hidden lg:inline-flex">
                       <button className="text-[12px] font-black uppercase tracking-widest text-slate-700 hover:text-slate-950 px-3 py-2 transition-colors">Log In</button>
                    </Link>
                    <Link to="/signup" className="hidden lg:inline-flex">
                       <Button size="sm" className="bg-slate-950 text-white h-10 px-6 text-[11px] font-black uppercase tracking-widest">Sign Up</Button>
                    </Link>
                  </div>
               )}
               <button
                 onClick={() => setIsOpen(!isOpen)}
                 aria-label={isOpen ? "Close menu" : "Open menu"}
                 aria-expanded={isOpen}
                 className="lg:hidden p-2 text-slate-950"
               >
                 {isOpen ? <X size={24} /> : <Menu size={24} />}
               </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-white border-t border-slate-100 overflow-hidden shadow-xl relative z-20"
            >
              <div className="py-6 px-4 flex flex-col gap-2 max-h-[70vh] overflow-y-auto">
                {navLinks.map((link) => (
                  <div key={link.label} className="border-b border-slate-50 last:border-0 pb-2">
                    {link.items ? (
                      <button 
                        onClick={() => setExpandedMobileMenu(expandedMobileMenu === link.label ? null : link.label)}
                        className="w-full flex justify-between items-center py-3 text-base font-black text-slate-900 uppercase tracking-widest"
                      >
                        {link.label}
                        <ChevronDown size={14} className={cn("transition-transform", expandedMobileMenu === link.label ? "rotate-180" : "opacity-30")} />
                      </button>
                    ) : (
                      <Link
                        to={link.href}
                        onClick={(e) => handleAnchorClick(e, link.href, (link as any).portal)}
                        className="w-full flex justify-between items-center py-3 text-base font-black text-slate-900 uppercase tracking-widest"
                      >
                        {link.label}
                      </Link>
                    )}
                    
                    <AnimatePresence>
                      {link.items && expandedMobileMenu === link.label && (
                        <motion.div 
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="pl-4 flex flex-col gap-1 pb-4"
                        >
                          {link.items.map((item: any) => (
                            <Link 
                              key={item.label} 
                              to={item.href} 
                              onClick={(e) => handleAnchorClick(e, item.href, item.portal)}
                              className="py-3 text-[12px] font-black uppercase tracking-widest text-slate-600 hover:text-slate-950"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>

              <div className="p-5 sm:p-6 bg-slate-50 flex flex-col gap-3">

                {!user ? (
                  <div className="flex flex-col sm:flex-row gap-2 mt-1">
                    <Link
                      to="/signup"
                      onClick={() => setIsOpen(false)}
                      className="flex-1 py-3.5 text-center text-[12px] font-black uppercase tracking-widest bg-slate-950 text-white rounded-sm"
                    >
                      Sign Up
                    </Link>
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="flex-1 py-3.5 text-center text-[12px] font-black uppercase tracking-widest border border-slate-200 bg-white text-slate-700 rounded-sm"
                    >
                      Log In
                    </Link>
                  </div>
                ) : (
                  <button
                    onClick={handleSignOut}
                    className="flex items-center justify-center gap-2 w-full py-3.5 text-[12px] font-black uppercase tracking-widest border border-slate-200 bg-white text-slate-700 rounded-sm"
                  >
                    <LogOut size={12} /> Sign Out
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Full-width Portal Switcher Bar - Professionally Spaced & Rounded */}
      <div className="w-full bg-white/95 backdrop-blur-md px-2 sm:px-4 py-2 sm:py-2.5 border-b border-slate-200/60 flex gap-2 sm:gap-4 shadow-inner relative z-10">
        <button 
          onClick={() => setPortal("tspsc")} 
           className={cn(
            "flex-1 h-10 sm:h-12 flex items-center justify-center gap-2 text-[10px] sm:text-[12px] font-black tracking-[0.2em] uppercase transition-all duration-500 rounded-sm sm:rounded-md",
            portal === "tspsc" 
              ? "bg-gradient-to-r from-amber-600 to-amber-500 text-white shadow-lg shadow-amber-900/20 ring-1 ring-amber-400/50" 
              : "bg-amber-50/50 text-amber-900/60 hover:bg-amber-50 hover:text-amber-900 border border-amber-100/50"
          )}
        >
          TSPSC PORTAL
        </button>

        <button 
          onClick={() => setPortal("appsc")} 
           className={cn(
            "flex-1 h-10 sm:h-12 flex items-center justify-center gap-2 text-[10px] sm:text-[12px] font-black tracking-[0.2em] uppercase transition-all duration-500 rounded-sm sm:rounded-md",
            portal === "appsc" 
              ? "bg-gradient-to-r from-blue-800 to-blue-700 text-white shadow-lg shadow-blue-900/20 ring-1 ring-blue-400/50" 
              : "bg-blue-50/50 text-blue-900/60 hover:bg-blue-50 hover:text-blue-900 border border-blue-100/50"
          )}
        >
          APPSC PORTAL
        </button>
      </div>
    </nav>
  )
}

export default Navbar

